import { useState, useContext } from 'react'
import { db, timestamp } from "../../firebase/config";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import { Auth } from "../../StateManagment/Auth";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined'
import Tooltip from "../common/Tooltip";
import QuestionnaireField from "./QuestionnaireField";
import QuestionTypeTag from "./QuestionTypeTag";
import ShowWhenChip from "./ShowWhenChip";
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import { v4 as uuid } from 'uuid'
import { client } from "../../hooks/Client";
import sortSectionFields from "../../helpers/questionnaire/sortSectionFields";
import isQualifyingQuestion from '../../helpers/questionnaire/isQualifyingQuestion'
import notDeleted from "../../helpers/notDeleted";
import removeFieldFromQuestionnaire, { removeFieldCopy } from "../../helpers/questionnaire/removeFieldFromQuestionnaire";
import Modal from "../common/Modal";
import scaleStartLabel from "../../helpers/questionnaire/scaleStartLabel";

// AI-CONTEXT: See docs/features/questionnaire-sections.md
// Renders the questions inside one section. Drag-and-drop is driven by the parent's
// `dragApi` so top-level and in-section rows share one model; ordering uses the
// client-side `sortSectionFields` (SectionPosition → Position fallback, no migration).
/**
 * @param {object} props
 * @param {string} props.section - ID van de sectie
 * @param {object} props.sectionField - Het QuestionnaireFields-document van de sectie
 * @param {object} props.dragApi - Sleepfuncties van de ouder
 * @param {Array<object>} props.questionnaireFields - Alle levende velden van de vragenlijst
 * @param {number} [props.firstNumber=1] - Nummer van de eerste vraag in deze sectie; vragen lopen door over de hele vragenlijst
 */
const OrderQuestionnaireSectionFields = ({ section, sectionField, dragApi, questionnaireFields, firstNumber = 1 }) => {
    // Context
    const [saved, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)

    // States
    const [editQuestion, setEditQuestion] = useState(null)
    const [fieldToRemove, setFieldToRemove] = useState(null)

    // The section's questions (soft-deleted excluded), ordered client-side.
    // Vragen in de sectie 'Kwalificering' zijn vergrendeld: alleen de schakelaar
    // op het onderzoek zet ze aan of uit. AI-CONTEXT: See docs/features/sector-kwalificeringsvragen.md
    const locked = isQualifyingQuestion(sectionField)
    const sectionFields = sortSectionFields((questionnaireFields || []).filter(f => f.SectionID === section && notDeleted(f)))

    // Handlers
    const titleHandler = (docid, value) => {
        db.collection('QuestionnaireFields').doc(docid).update({ Question: value })
            .then(() => setSaved(true))
    }

    // Eigen vraag: soft delete (ADR-008). Meetstandaardvraag: loskoppelen.
    // AI-CONTEXT: See docs/features/msi-effect-questionnaire-attach.md
    // Eerst bevestigen (testronde 2026-09-10, B1); dan pas schrijven.
    const deleteQuestionHandler = (field) => setFieldToRemove(field)

    const confirmRemove = () => {
        const field = fieldToRemove
        if (!field) return
        setFieldToRemove(null)
        db.collection('QuestionnaireFields').doc(field.docid).update(
            removeFieldFromQuestionnaire(field, { timestamp, userId: auth?.ID })
        )
            .then(() => setSaved(true))
            .catch(err => console.log(err))
    }

    const editHandler = (id) => setEditQuestion(prev => (prev === id ? null : id))

    // Add a blank question directly inside this section.
    const addQuestionToSection = () => {
        db.collection('QuestionnaireFields').doc().set({
            Compagny: client,
            CompagnyID: client,
            ID: uuid(),
            Timestamp: timestamp,
            QuestionnaireID: sectionField.QuestionnaireID,
            Type: 'paragraph',
            Question: '',
            Explainer: '',
            ReachStart: 0,
            ReachStartLable: '',
            ReachEnd: 5,
            ReachEndLabel: '',
            Key: uuid(),
            Position: (questionnaireFields?.length || 0) + 1,
            SectionPosition: sectionFields.length + 1,
            Multiple: [],
            SectionID: section,
            LiveReportHidden: false,
        }).then(() => setSaved(true))
    }

    const duplicateHandler = (field) => {
        const fieldID = uuid()

        db.collection('QuestionnaireFields').doc().set({
            Compagny: client,
            CompagnyID: client,
            ID: fieldID,
            Timestamp: timestamp,
            QuestionnaireID: field.QuestionnaireID,
            Type: field.Type,
            Question: ('Kopie van ' + field.Question),
            Explainer: field.Explainer,
            ReachStart: field.ReachStart,
            ReachStartLable: scaleStartLabel(field),
            ReachEnd: field.ReachEnd,
            ReachEndLabel: field.ReachEndLabel,
            Position: field.Position,
            SectionPosition: sectionFields.length + 1,
            Multiple: field.Multiple,
            SectionID: field.SectionID,
            MatrixBipolar: field.MatrixBipolar === true,
        }).then(() => {
            duplicateOptions(field.ID, fieldID, field.Question)
            duplicateMatrixRows(field.ID, fieldID, field.Question)
            duplicateMatrixColumns(field.ID, fieldID, field.Question)
        })
    }

    const duplicateOptions = (id, fieldID, question) => {
        db.collection('MultipleQuestionOptions')
            .where('CompagnyID', '==', client)
            .where('Field', '==', id)
            .get()
            .then((snapshot) => {
                snapshot.docs.filter(doc => notDeleted(doc.data())).forEach(doc => {
                    db.collection('MultipleQuestionOptions').doc().set({
                        Option: doc.data().Option,
                        Question: question,
                        Field: fieldID,
                        Compagny: client,
                        CompagnyID: client,
                        Timestamp: timestamp,
                        Position: doc.data().Position,
                        ID: uuid(),
                    })
                })
            })
    }

    const duplicateMatrixRows = (id, fieldID, question) => {
        db.collection('MatrixQuestionRows')
            .where('Compagny', '==', client)
            .where('Field', '==', id)
            .get()
            .then((snapshot) => {
                snapshot.docs.filter(doc => notDeleted(doc.data())).forEach(doc => {
                    db.collection('MatrixQuestionRows').doc().set({
                        Title: doc.data().Title,
                        TitleRight: doc.data().TitleRight || '',
                        Question: question,
                        Field: fieldID,
                        Compagny: client,
                        CompagnyID: client,
                        Timestamp: timestamp,
                        Position: doc.data().Position,
                        ID: uuid(),
                    })
                })
            })
    }

    const duplicateMatrixColumns = (id, fieldID, question) => {
        db.collection('MatrixQuestionColumns')
            .where('Compagny', '==', client)
            .where('Field', '==', id)
            .get()
            .then((snapshot) => {
                snapshot.docs.filter(doc => notDeleted(doc.data())).forEach(doc => {
                    db.collection('MatrixQuestionColumns').doc().set({
                        Title: doc.data().Title,
                        Question: question,
                        Field: fieldID,
                        Compagny: client,
                        CompagnyID: client,
                        Timestamp: timestamp,
                        Position: doc.data().Position,
                        ID: uuid(),
                    })
                })
            })
    }

    // Empty-section dropzone forces an "inside" drop (a question joins this section).
    const onEmptyZoneOver = (e) => {
        e.preventDefault()
        e.stopPropagation()
        const dragged = dragApi.draggedRef.current
        if (!dragged || dragged.Type === 'section') return
        dragApi.setDropIndicator({ docid: sectionField.docid, edge: 'inside' })
    }

    const onEmptyZoneDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        const dragged = dragApi.draggedRef.current
        dragApi.clearDrag()
        if (!dragged || dragged.Type === 'section') return
        dragApi.performDrop(dragged, sectionField, 'inside')
    }

    const removeCopy = fieldToRemove ? removeFieldCopy(fieldToRemove) : null

    return (
        <>
            <Modal
                modalOpen={fieldToRemove !== null}
                setModalOpen={(open) => { if (!open) setFieldToRemove(null) }}
                pageHeader={removeCopy?.title || ''}
                confirmLabel={removeCopy?.confirmLabel || ''}
                confirm={confirmRemove}
                content={<p>{removeCopy?.body || ''}</p>}
            />
            {sectionFields.map((field, index) => (
                <div
                    key={field.ID}
                    className={`order-questionnaire-field-item-container ${dragApi.dropClass(field.docid)}`}
                    onDragOver={dragApi.onRowDragOver(field)}
                    onDrop={dragApi.onRowDrop(field)}
                >
                    <div className={`qb-q ${editQuestion === field.ID ? 'open' : ''} ${field.ShowWhen?.FieldID ? 'qb-conditional' : ''}`}>
                        <div className="qb-q-row">
                            {!locked && (
                                <Tooltip content="Versleep vraag" width={'24px'}>
                                    <span className="qb-grip" draggable='true' onDragStart={dragApi.onRowDragStart(field)} onDragEnd={dragApi.onRowDragEnd}>
                                        <DragIndicatorOutlinedIcon />
                                    </span>
                                </Tooltip>
                            )}
                            <span className="qb-num">{firstNumber + index}</span>
                            {locked ? (
                                <span className="qb-q-title qb-locked-title">{field.Question}</span>
                            ) : (
                                <input className="qb-q-title" type="text" defaultValue={field.Question} placeholder="Typ hier je vraag…" onChange={(e) => titleHandler(field.docid, e.target.value)} />
                            )}
                            <ShowWhenChip field={field} allFields={questionnaireFields} />
                            <QuestionTypeTag type={field.Type} />
                            {!locked && (
                            <div className="qb-actions">
                                <Tooltip content="Bewerken" width={'32px'}>
                                    <button className="qb-iconbtn primary" onClick={() => editHandler(field.ID)} aria-label="Bewerken">
                                        <EditOutlinedIcon />
                                    </button>
                                </Tooltip>
                                <Tooltip content="Dupliceren" width={'32px'}>
                                    <button className="qb-iconbtn" onClick={() => duplicateHandler(field)} aria-label="Dupliceren">
                                        <ContentCopyOutlinedIcon />
                                    </button>
                                </Tooltip>
                                <Tooltip content="Verwijder vraag" width={'32px'}>
                                    <button className="qb-iconbtn danger" onClick={() => deleteQuestionHandler(field)} aria-label="Verwijder vraag">
                                        <DeleteOutlinedIcon />
                                    </button>
                                </Tooltip>
                            </div>
                            )}
                        </div>
                        {editQuestion === field.ID &&
                            <QuestionnaireField field={field} index={index} position={sectionFields.length} allFields={questionnaireFields} />
                        }
                    </div>
                </div>
            ))}
            {!locked && (
            <div
                id='section-dropzone'
                className={`${sectionFields.length === 0 ? 'qb-dropzone-visible' : 'qb-dropzone-hidden'} ${dragApi.dropClass(sectionField.docid) === 'qb-drop-inside' ? 'qb-drop-inside' : ''}`}
                onDragOver={onEmptyZoneOver}
                onDrop={onEmptyZoneDrop}
                onClick={addQuestionToSection}
            >
                <div id='dropzone-title-container'>
                    <AddOutlinedIcon />
                    <p>Sleep een vraag hierheen of voeg er een toe</p>
                </div>
            </div>
            )}
        </>
    )
}

export default OrderQuestionnaireSectionFields
